import React, { useState, useEffect } from "react";
import { FaDownload, FaTrash, FaFileAlt, FaCloudUploadAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import configData from "../../configure";
import Select from "react-select";
import { Snackbar, Alert, Button } from "@mui/material";
import "./AcademicDocument.css";
import { FaCalendarAlt } from "react-icons/fa";
import { uploadFileToCampusFileSystem, getFilesByEntity, deleteFile, getFileById, downloadCampusFile } from "../../services/CampusFileSystemService";
import { useAuth } from "../../AuthContext";

function AcademicDocument({ classId, token, axios, orgId }) {
  const { userRole } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("All Courses");
  const [selectedFileType, setSelectedFileType] = useState("All File Types");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [showClassDropdown, setShowClassDropdown] = useState(false);

  const [uploadData, setUploadData] = useState({
    file: null,
    remarks: "",
    fileCategory: "",
    fileVisibility: "ORG_WIDE",
    academicYear: "2025-2026",
    classIds: [],
    courseId: "",
  });

  // 🧡 For delete confirmation snackbar
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    fileId: null,
  });

  const fileTypeOptions = [
    "All File Types",
    "DOCUMENT",
    "IMAGE",
    "VIDEO",
    "AUDIO",
    "SPREADSHEET",
    "ZIP",
  ];

  const fileCategoryOptions = [
    { value: "ASSIGNMENT", label: "Assignment" },
    { value: "STUDY_MATERIAL", label: "Study Material" },
    { value: "EXAM_PAPER", label: "Exam Paper" },
    { value: "LECTURE_NOTES", label: "Notes" },
    { value: "SYLLABUS", label: "Syllabus" },
    { value: "OTHER", label: "Question Bank" },
    { value: "OTHER", label: "General Document" },
  ];

  // 🧠 Fetch courses & classes
  useEffect(() => {
    if (!orgId || !token) return;

    const fetchData = async () => {
      try {
        const courseRes = await axios.get(`${configData.GET_COURSES}/${orgId}`, {
          headers: { Authorization: token },
        });

        const uniqueCourses = Array.isArray(courseRes.data)
          ? courseRes.data.filter(
            (course, index, self) =>
              index ===
              self.findIndex(
                (c) =>
                  (c.courseDetails?.courseName || c.courseCode) ===
                  (course.courseDetails?.courseName || course.courseCode)
              )
          )
          : [];

        setCourses(uniqueCourses);

        const classRes = await axios.get(`${configData.CLASS_BY_ORG}/${orgId}`, {
          headers: { Authorization: token },
        });

        setClasses(Array.isArray(classRes.data) ? classRes.data : []);
      } catch (error) {
        console.error("Error fetching courses/classes:", error);
        if (error.response) {
          console.error("Error fetching courses/classes response data:", error.response.data);
        }
        toast.error("Failed to load course/class data");
      }
    };

    fetchData();
  }, [orgId, token, axios]);

  // 📂 Fetch documents
  useEffect(() => {
    if (classId && token) fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, token, orgId]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
      const res = await getFilesByEntity(classId, rawToken, 0, 100);
      const fileList =
        Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
            ? res.items
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res?.content)
                ? res.content
                : [];

      setDocuments(fileList);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setIsLoading(false);
    }
  };

  // 💾 Handle Upload
  const handleUpload = async () => {
    if (!uploadData.file) return toast.error("Please select a file before uploading!");
    if (!uploadData.fileCategory) return toast.error("Please select a document category!");
    if (!uploadData.classIds?.length && !uploadData.courseId)
      return toast.error("Please select at least one class or course!");

    try {
      const selectedClassIds =
        uploadData.classIds.length > 0
          ? uploadData.classIds.map((c) => c.value)
          : [classId];

      const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
      const uploadPromises = selectedClassIds.map(async (targetClassId) => {
        const record = await uploadFileToCampusFileSystem(uploadData.file, {
          entityType: "CLASS",
          entityId: targetClassId,
          orgId: orgId || "1",
          category: uploadData.fileCategory || "STUDY_MATERIAL",
          remarks: uploadData.remarks || undefined,
          uploadedBy: "admin",
          token: rawToken,
        });

        try {
          const downloadUrl = record?.downloadUrl;
          const bodyText = `New academic document "${uploadData.file.name}" has been published for your class.\n\n` +
            (uploadData.remarks ? `Remarks: ${uploadData.remarks}\n\n` : '') +
            (downloadUrl ? `📎 Document: [${uploadData.file.name}](${downloadUrl})` : '');

          const authHeader = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
          await axios.post(configData.INBOX_SEND, {
            subject: `📄 Academic Document: ${uploadData.file.name}`,
            body: bodyText,
            orgId: orgId || "1",
            targetType: "CLASS",
            targetId: targetClassId,
            category: "ANNOUNCEMENT",
            attachmentUrls: downloadUrl ? [downloadUrl] : undefined
          }, {
            headers: { Authorization: authHeader }
          });
        } catch (inboxErr) {
          console.warn("Failed to send inbox notification for document:", inboxErr);
        }

        return record;
      });

      await Promise.all(uploadPromises);

      toast.success(`🎉 Uploaded successfully to ${selectedClassIds.length} class(es)!`);
      setUploadModalOpen(false);
      setUploadData({
        file: null,
        remarks: "",
        fileCategory: "",
        fileVisibility: "ORG_WIDE",
        academicYear: "2025-2026",
        classIds: [],
        courseId: "",
      });
      fetchDocuments();
    } catch (error) {
      console.error("❌ Upload error:", error);
      if (error.response) {
        console.error("❌ Backend Error Data:", error.response.data);
      }
      toast.error(
        error.response?.data?.message || "Upload failed. Try again after refresh."
      );
    }
  };

  // 💾 Handle Download
  const handleDownload = async (doc) => {
    const fileId = doc.fileId || doc.id;
    const displayName = doc.originalFileName || doc.filename || "Document";
    if (!fileId) {
      toast.error("Invalid document ID");
      return;
    }
    try {
      toast.info(`Downloading ${displayName}...`);
      await downloadCampusFile(fileId, displayName, token);
      toast.success(`Downloaded ${displayName}`);
    } catch (error) {
      console.error("Failed to download document:", error);
      toast.error(error.message || "Failed to download document");
    }
  };

  // 🗑️ Delete File (actual delete logic)
  const handleDelete = async (fileId) => {
    try {
      const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
      await deleteFile(fileId, rawToken);

      toast.success("🗑️ Document deleted successfully!", {
        className: "toast-delete-success",
      });

      fetchDocuments();
    } catch (error) {
      console.error("❌ Delete error:", error);
      toast.error("Error deleting file!");
    }
  };


  // 🔎 Filtering
  // 🔎 Smart Filtering (by course, file type, and search)
const filteredDocs = documents.filter((doc) => {
  const fileName = doc.originalFileName?.toLowerCase() || "";
  const extension = fileName.split(".").pop();

  // 🧠 Filter by course
  const matchesCourse =
    selectedCourse === "All Courses" || doc.relatedEntityId === selectedCourse;

  // 🎯 Filter by file type (either fileType or file extension)
  const matchesType =
    selectedFileType === "All File Types" ||
    (doc.fileType || "DOCUMENT") === selectedFileType ||
    (selectedFileType === "ZIP" && extension === "zip") ||
    (selectedFileType === "IMAGE" && ["jpg", "jpeg", "png"].includes(extension)) ||
    (selectedFileType === "VIDEO" && ["mp4", "mov", "avi"].includes(extension)) ||
    (selectedFileType === "AUDIO" && ["mp3", "wav", "aac"].includes(extension)) ||
    (selectedFileType === "SPREADSHEET" && ["xls", "xlsx", "csv"].includes(extension)) ||
    (selectedFileType === "DOCUMENT" && ["pdf", "doc", "docx", "txt"].includes(extension));

  // 🔍 Filter by search
  const matchesSearch =
    !searchTerm ||
    fileName.includes(searchTerm.toLowerCase()) ||
    doc.remarks?.toLowerCase().includes(searchTerm.toLowerCase());

  return matchesCourse && matchesType && matchesSearch;
});


  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="academic-documents-container">
      {/* 🔍 Filters */}
      <div className="filters-row">
        <div className="filter-group">
          <label className="form-label" htmlFor="courseSelect">
            Select Course
          </label>
          <select
            id="courseSelect"
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="form-select"
          >
            <option>All Courses</option>
            {courses.map((course) => (
              <option key={course.courseId} value={course.courseId}>
                {course.courseDetails?.courseName || course.courseCode}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label" htmlFor="fileTypeSelect">
            Select File Type
          </label>
          <select
            id="fileTypeSelect"
            value={selectedFileType}
            onChange={(e) => setSelectedFileType(e.target.value)}
            className="form-select"
          >
            {fileTypeOptions.map((type) => (
              <option key={type}>{type}</option>
            ))}
          </select>
        </div>

        <input
          type="text"
          placeholder="Search Documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "250px",
            padding: "8px 12px",
            borderRadius: "6px",
            border: "1px solid #ccc",
            fontSize: "14px",
            outline: "none",
            marginTop: "4px", // adjust this if you want to align it vertically with others
          }}
        />



        {userRole !== 'STUDENT' && (
          <button className="upload-btn" onClick={() => setUploadModalOpen(true)}>
            <FaCloudUploadAlt /> Upload Document
          </button>
        )}
      </div>

      {/* 🗂️ Documents */}
      <h3>Class Documents ({filteredDocs.length})</h3>

      {isLoading ? (
        <p>Loading documents...</p>
      ) : filteredDocs.length === 0 ? (
        <p className="no-documents">
          {documents.length === 0
            ? "No documents available. Upload your first one!"
            : "No documents match your filters."}
        </p>
      ) : (
        <ul className="documents-list">
          {filteredDocs.map((doc) => (
            <li key={doc.fileId} className="document-item">
              <div className="doc-info">
                <FaFileAlt className="doc-icon" />
                <div className="doc-text">
                  <strong>{doc.originalFileName}</strong>
                  <p>{doc.remarks || "No description"}</p>
                  <div className="doc-meta">
                    <span className="date">
                      <FaCalendarAlt className="calendar-icon" />
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </span>
                    <span className="file-size">{formatFileSize(doc.fileSize)}</span>
                  </div>
                </div>

              </div>
              <div className="doc-actions">
                <button
                  type="button"
                  onClick={() => handleDownload(doc)}
                  className="download-icon-btn"
                  title="Download Document"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#014486', fontSize: '16px', display: 'inline-flex', alignItems: 'center', padding: '4px' }}
                >
                  <FaDownload />
                </button>
                {userRole !== 'STUDENT' && (
                  <FaTrash
                    className="delete-icon"
                    onClick={() => setConfirmDelete({ open: true, fileId: doc.fileId })}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* 📤 Upload Modal */}
      {uploadModalOpen && (
        <div
          className="upload-modal-overlay"
          onClick={(e) => {
            if (e.target.className === "upload-modal-overlay")
              setUploadModalOpen(false);
          }}
        >
          <div className="upload-modal">
            <h3 className="modal-title">Upload Document</h3>

            <label className="form-label">
              Select Class <span className="required">*</span>
            </label>
            <div className="custom-multiselect">
              <div
                className="multiselect-input"
                onClick={() => setShowClassDropdown((prev) => !prev)}
              >
                {uploadData.classIds.length > 0
                  ? `${uploadData.classIds.length} class selected`
                  : "Select one or more classes..."}
                <span className="arrow">{showClassDropdown ? "▲" : "▼"}</span>
              </div>

              {showClassDropdown && (
                <div className="multiselect-dropdown">
                  {classes.map((cls) => {
                    const isChecked = uploadData.classIds.some(
                      (c) => c.value === cls.classId
                    );
                    return (
                      <label key={cls.classId} className="multiselect-option">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            let newSelection;
                            if (e.target.checked) {
                              newSelection = [
                                ...uploadData.classIds,
                                {
                                  value: cls.classId,
                                  label: (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined')
                                    ? `${cls.className} - ${cls.sectionName}`
                                    : `${cls.className || ''}`.trim(),
                                },
                              ];
                            } else {
                              newSelection = uploadData.classIds.filter(
                                (c) => c.value !== cls.classId
                              );
                            }
                            setUploadData({ ...uploadData, classIds: newSelection });
                          }}
                        />
                        {cls.className} - {cls.sectionName}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <label className="form-label">Select Course</label>
            <select
              value={uploadData.courseId}
              onChange={(e) =>
                setUploadData({ ...uploadData, courseId: e.target.value })
              }
              className="form-select"
            >
              <option value="">-- Select Course --</option>
              {courses.map((course) => (
                <option key={course.courseId} value={course.courseId}>
                  {course.courseDetails?.courseName || course.courseCode}
                </option>
              ))}
            </select>

            <label className="form-label">
              Select File <span className="required">*</span>
            </label>
            <div className="file-upload-box">
              <input
                type="file"
                id="file-input"
                onChange={(e) =>
                  setUploadData({ ...uploadData, file: e.target.files[0] })
                }
                style={{ display: "none" }}
              />
              <label
                htmlFor="file-input"
                className="file-label"
                style={{ color: "#2563eb", cursor: "pointer" }}
              >
                <FaCloudUploadAlt size={24} style={{ color: "#2563eb" }} />
                {uploadData.file ? uploadData.file.name : "Choose a file..."}
              </label>
            </div>

            <label className="form-label">
              Document Category <span className="required">*</span>
            </label>
            <select
              value={uploadData.fileCategory}
              onChange={(e) =>
                setUploadData({ ...uploadData, fileCategory: e.target.value })
              }
              className="form-select"
            >
              <option value="">-- Select Category --</option>
              {fileCategoryOptions.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            <label className="form-label">Remarks</label>
            <textarea
              placeholder="Enter details..."
              value={uploadData.remarks}
              onChange={(e) =>
                setUploadData({ ...uploadData, remarks: e.target.value })
              }
              rows={3}
            />

            <div className="modal-actions">
              <button
                onClick={() => setUploadModalOpen(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button onClick={handleUpload} className="upload-confirm-btn">
                <FaCloudUploadAlt /> Upload to{" "}
                {uploadData.classIds.length || ""} Class
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ Delete Confirmation Snackbar */}
      <Snackbar
        open={confirmDelete.open}
        autoHideDuration={4000}
        onClose={() => setConfirmDelete({ open: false, fileId: null })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="warning"
          sx={{ width: "100%" }}
          onClose={() => setConfirmDelete({ open: false, fileId: null })}
          action={
            <Button
              color="error"
              size="small"
              onClick={() => {
                handleDelete(confirmDelete.fileId);
                setConfirmDelete({ open: false, fileId: null });
              }}
            >
              DELETE
            </Button>
          }
        >
          Are you sure you want to delete this document?
        </Alert>
      </Snackbar>


    </div>
  );
}

export default AcademicDocument;

